/**
 * MIO — deteção textual leve de Red Flags / Precauções clínicas.
 *
 * Pure function · sem IA · sem chamada backend · sem dependências.
 * Lê os campos de anamnese já existentes no objeto `patient` (intake) e
 * cruza com regex prudentes em PT-PT.
 *
 * SAÍDA: { status: 'green' | 'amber' | 'red', flags: Flag[] }
 *
 *   Flag = {
 *     severity: 'amber' | 'red',
 *     label: string,            // título curto em PT-PT, não-diagnóstico
 *     source: string,           // campo da anamnese de onde veio
 *     matrixCardId: string,     // id na MIO Matrix → Precauções
 *     hint?: string,            // pequena nota não-diagnóstica
 *   }
 *
 * IMPORTANTE: NÃO diagnostica, NÃO bloqueia, NÃO decide. Apenas sinaliza
 * para que o osteopata pondere a sua intervenção. A autoridade clínica é
 * sempre do profissional. Falsos positivos são clinicamente preferíveis a
 * falsos negativos no contexto de prudência.
 */

// Campos de texto livre habituais na anamnese MIO. Outros campos podem ser
// adicionados sem partir compatibilidade.
const TEXT_FIELDS = [
  "chief_complaint",
  "aggravating_factors",
  "relieving_factors",
  "previous_injuries",
  "surgeries",
  "chronic_conditions",
  "medication",
  "lifestyle",
  "onset",
];

// Catálogo de regras. Cada regra é avaliada contra o texto agregado da
// anamnese; as primeiras correspondências dominam o `status` global.
const RULES = [
  // ─── RED — exigem prudência reforçada / referenciação ─────────────────
  { id: "onco", severity: "red", label: "Antecedentes oncológicos referidos",
    matrixCardId: "precaucoes-oncologicas",
    re: /(onco(l[óo]gic[oa]s?)?|c[âa]ncer|cancer|cancro|tumor\w*|metas?ta\w*|quimio\w*|radio\s?terapia|leucemia|linfoma)/i,
    hint: "Recomenda-se evitar técnicas de alta velocidade e validar com a equipa médica." },

  { id: "neuro-progressivo", severity: "red", label: "Sinais neurológicos progressivos",
    matrixCardId: "precaucoes-neurologicas",
    re: /(perda\s+de\s+for[çc]a\s+s[úu]bita|altera[çc][õo]es\s+(de\s+)?(fala|vis[ãa]o|degluti[çc][ãa]o)|d[ée]ficit\s+neurol|parestesia\s+progress|hipoestesia\s+progress)/i,
    hint: "Compatível com necessidade de avaliação neurológica adicional." },

  { id: "cauda-equina", severity: "red", label: "Sinais sugestivos de cauda equina",
    matrixCardId: "precaucoes-neurologicas",
    re: /(cauda\s+equina|anestesia\s+em\s+sela|incontin[êe]ncia\s+(urin|fecal|esfincter)|reten[çc][ãa]o\s+urin[áa]ria\s+aguda)/i,
    hint: "Quadro com critério para referenciação clínica imediata." },

  { id: "avc-ait", severity: "red", label: "Suspeita de evento vascular cerebral",
    matrixCardId: "precaucoes-vasculares",
    re: /\b(AVC|AIT|acidente\s+vascular|isquemia\s+cerebral|drop\s+attack)\b/i,
    hint: "Evitar manipulação cervical até validação médica." },

  { id: "tvp-ep", severity: "red", label: "Antecedentes de TVP / embolia pulmonar",
    matrixCardId: "precaucoes-vasculares",
    re: /(trombose\s+venosa\s+profunda|\bTVP\b|embolia\s+pulmonar|\bTEP\b)/i },

  { id: "aneurisma", severity: "red", label: "Aneurisma referido",
    matrixCardId: "precaucoes-vasculares",
    re: /aneurisma/i },

  { id: "angina-instavel", severity: "red", label: "Angina instável referida",
    matrixCardId: "precaucoes-vasculares",
    re: /angina\s+inst[áa]vel/i },

  { id: "fratura", severity: "red", label: "Suspeita / antecedente recente de fratura",
    matrixCardId: "precaucoes-osseas-articulares",
    re: /(fratura\s+(recente|suspeita)|suspeita\s+de\s+fratura|fissura\s+[óo]ssea)/i },

  { id: "infecao", severity: "red", label: "Sinais sugestivos de infeção sistémica",
    matrixCardId: "precaucoes-gerais",
    re: /(febre\s+(persistente|alta)|infe[çc][ãa]o\s+sist[êe]mica|sepse|infe[çc][ãa]o\s+[óo]ssea|osteomielite|artrite\s+s[ée]ptica)/i },

  { id: "perda-peso", severity: "red", label: "Perda de peso inexplicada",
    matrixCardId: "precaucoes-oncologicas",
    re: /(perda\s+(de\s+)?peso\s+(inexplic|n[ãa]o\s+intencional)|emagrecimento\s+(inexplic|r[áa]pido))/i },

  { id: "dor-noturna", severity: "red", label: "Dor noturna não mecânica",
    matrixCardId: "precaucoes-gerais",
    re: /(dor\s+noturna|dor\s+que\s+acorda|dor\s+que\s+desperta)/i },

  { id: "mielopatia", severity: "red", label: "Sinais sugestivos de mielopatia",
    matrixCardId: "precaucoes-neurologicas",
    re: /(mielopatia|hiperreflexia\s+global|babinski\s+positivo|cl[óo]nus)/i },

  { id: "instabilidade", severity: "red", label: "Instabilidade ligamentar / vertebral",
    matrixCardId: "precaucoes-osseas-articulares",
    re: /(instabilidade\s+(ligamentar|vertebral|articular|atlanto)|sinal\s+de\s+sharp.?purser)/i },

  { id: "osteoporose-severa", severity: "red", label: "Osteoporose severa",
    matrixCardId: "precaucoes-osseas-articulares",
    re: /osteoporose\s+(severa|grave|av[ae]n[çc]ada)/i,
    hint: "Evitar técnicas de alta velocidade e cargas axiais." },

  // ─── AMBER — precauções relativas ────────────────────────────────────
  { id: "gravidez", severity: "amber", label: "Gravidez",
    matrixCardId: "precaucoes-relativas",
    re: /\b(gravid[ae]z|gr[áa]vida|gestante|gestacao|gesta[çc][ãa]o)\b/i,
    hint: "Adaptar posicionamento; evitar técnicas abdominais profundas e HVLA lombo-pélvica." },

  { id: "hta", severity: "amber", label: "Hipertensão (controlo a confirmar)",
    matrixCardId: "precaucoes-vasculares",
    re: /(hipertens[ãa]o\s+(n[ãa]o\s+controlada|descontrol)|HTA\s+(n[ãa]o\s+controlada|descontrol)|press[ãa]o\s+arterial\s+elevada)/i },

  { id: "anticoag", severity: "amber", label: "Medicação anticoagulante / risco hemorrágico",
    matrixCardId: "precaucoes-vasculares",
    re: /(anticoagul|varfarin|warfarin|rivaroxaban|apixaban|enoxaparin|heparina|dabigatran|aspirina\s+a\s+longo)/i },

  { id: "diabetes-neuro", severity: "amber", label: "Diabetes com neuropatia referida",
    matrixCardId: "precaucoes-neurologicas",
    re: /(diabetes.{0,40}neuropat|neuropatia\s+diab[ée]tica)/i },

  { id: "protese", severity: "amber", label: "Próteses / implantes",
    matrixCardId: "precaucoes-osseas-articulares",
    re: /(pr[óo]tese\s+(da\s+anca|do\s+joelho|articular|total)|implante\s+(articular|met[áa]lico)|artroplastia)/i },

  { id: "hernia-disc", severity: "amber", label: "Hérnia discal aguda referida",
    matrixCardId: "contra-hvla-direta",
    re: /(h[ée]rnia\s+discal\s+(aguda|recente)|protus[ãa]o\s+discal\s+sintom)/i },

  { id: "whiplash", severity: "amber", label: "Whiplash recente",
    matrixCardId: "precaucoes-cervicais",
    re: /(whiplash|chicotada\s+cervical|trauma\s+cervical\s+recente)/i },

  { id: "hipermobil", severity: "amber", label: "Hipermobilidade",
    matrixCardId: "precaucoes-osseas-articulares",
    re: /(hipermobil|s[íi]ndrome\s+de\s+ehlers|laxid[ãa]o\s+articular)/i },

  { id: "vertigem-cerv", severity: "amber", label: "Vertigem associada a movimento cervical",
    matrixCardId: "precaucoes-cervicais",
    re: /(vertigem.{0,40}cervic|tontura.{0,40}cervic|insufici[êe]ncia\s+vertebro.?basilar)/i },

  { id: "osteopenia", severity: "amber", label: "Osteopenia",
    matrixCardId: "precaucoes-osseas-articulares",
    re: /osteopen/i },

  { id: "artrite-inflam", severity: "amber", label: "Artrite inflamatória ativa",
    matrixCardId: "precaucoes-osseas-articulares",
    re: /(artrite\s+(reumat|psori|inflam)|espondilite\s+anquilos|l[úu]pus|esclerose\s+sist[êe]mica)/i },

  { id: "cirurgia-recente", severity: "amber", label: "Cirurgia recente",
    matrixCardId: "precaucoes-pos-cirurgicas",
    re: /(cirurgia\s+(recente|h[áa]\s+\d+\s*(semana|mes))|p[óo]s.?operat[óo]rio\s+recente)/i },

  { id: "idade-fragil", severity: "amber", label: "Perfil frágil / idade avançada referida",
    matrixCardId: "precaucoes-osseas-articulares",
    re: /(idoso\s+fr[áa]gil|fragilidade|idade\s+avan[çc]ada)/i },

  { id: "kinesiofobia", severity: "amber", label: "Kinesiofobia / medo do movimento",
    matrixCardId: "consentimento-limites",
    re: /(kinesiofobia|medo\s+do\s+movimento|ansiedade\s+(intensa|severa)\s+ao\s+movimento)/i },
];

function aggregateText(patient) {
  if (!patient || typeof patient !== "object") return "";
  const parts = [];
  for (const k of TEXT_FIELDS) {
    const v = patient[k];
    if (typeof v === "string" && v.trim()) parts.push(v);
  }
  return parts.join(" \n ");
}

/**
 * detectRedFlags(patient, analysis?) → { status, flags }
 *
 * O parâmetro `analysis` está aceite para extensões futuras (e.g. cruzar
 * com `findings` da análise) mas é opcional. Hoje, a deteção é puramente
 * textual sobre a anamnese.
 */
export function detectRedFlags(patient /* , analysis */) {
  const text = aggregateText(patient);
  if (!text) return { status: "green", flags: [] };

  const flags = [];
  const seen = new Set();
  for (const rule of RULES) {
    if (seen.has(rule.id)) continue;
    if (rule.re.test(text)) {
      flags.push({
        id: rule.id,
        severity: rule.severity,
        label: rule.label,
        matrixCardId: rule.matrixCardId,
        hint: rule.hint || null,
      });
      seen.add(rule.id);
    }
  }

  let status = "green";
  if (flags.some(f => f.severity === "red")) status = "red";
  else if (flags.length > 0) status = "amber";

  return { status, flags };
}

/** Util: copy estática por estado para o painel. */
export const STATUS_COPY = {
  green: {
    title: "Sem precauções clínicas relevantes identificadas",
    body: "Não foram identificadas, nos dados atualmente registados, precauções clínicas relevantes. A decisão terapêutica permanece dependente da avaliação manual, consentimento informado e raciocínio clínico do osteopata.",
    footnote: "Esta informação não substitui avaliação clínica presencial, consentimento informado nem raciocínio profissional.",
  },
  amber: {
    title: "Precauções relativas identificadas",
    body: "Foram identificados fatores que podem exigir adaptação da intensidade, técnica ou posicionamento. Recomenda-se validação manual antes de intervenção física.",
    footnote: "A leitura é sugestiva e não-diagnóstica. A decisão final é do osteopata responsável.",
  },
  red: {
    title: "Precauções reforçadas — atenção sugerida",
    body: "Foram identificados dados compatíveis com necessidade de prudência reforçada. Recomenda-se avaliação clínica adicional, eventual referenciação ou adaptação/adiamento da intervenção, conforme julgamento profissional.",
    footnote: "MIO não diagnostica nem decide a intervenção. A autoridade clínica é sempre do osteopata.",
  },
};
